import { ANALYTICS_ACTIVITY_EVENTS, ANALYTICS_CONFIG, ANALYTICS_SESSION_KEY } from "../utils/const";
import { getAnalyticsTrackingKey, sendAnalyticsEvents } from "../utils/ApiHandler";

// SCAI activity tracker, exposed as window.__ANALYTICS__. Events are queued
// and sent to analytics_service once a minute, and right away on logout,
// business-unit switch and when the tab is hidden or closed. Active time is
// visible-tab time with user activity in the last IDLE_TIMEOUT_MS.

const uuid = () =>
  globalThis.crypto?.randomUUID?.() ??
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.trunc(Math.random() * 16);
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });

const storedSession = {
  read() {
    try {
      return JSON.parse(sessionStorage.getItem(ANALYTICS_SESSION_KEY) || "null");
    } catch {
      return null;
    }
  },
  write(value) {
    try {
      if (value) sessionStorage.setItem(ANALYTICS_SESSION_KEY, JSON.stringify(value));
      else sessionStorage.removeItem(ANALYTICS_SESSION_KEY);
    } catch {
      // storage unavailable: the session just won't survive a reload
    }
  },
};

export function createTracker({ getKey = getAnalyticsTrackingKey, sendEvents = sendAnalyticsEvents, now = () => Date.now() } = {}) {
  const { FLUSH_INTERVAL_MS, IDLE_TIMEOUT_MS, TICK_MS, RESUME_AFTER_MS, MAX_BATCH, MAX_QUEUE, MAX_ACTIVE_MS_PER_EVENT } =
    ANALYTICS_CONFIG;

  let session = null; // { userId, tenantId, buId, sessionId, anonymousId, apiKey }
  let queue = []; // { apiKey, event }
  let page = null; // { url, context }
  let keyRequest = 0;
  let pendingActiveMs = 0;
  let lastActivityAt = now();
  let lastTickAt = now();
  let lastSentAt = now();
  let timers = [];
  let listening = false;

  const tick = () => {
    const at = now();
    const until = Math.min(at, lastActivityAt + IDLE_TIMEOUT_MS);
    if (document.visibilityState !== "hidden" && until > lastTickAt) {
      pendingActiveMs += Math.min(until - lastTickAt, TICK_MS * 3); // ignore sleep / frozen tabs
    }
    lastTickAt = at;
  };

  const takeActiveMs = () => {
    tick();
    const ms = Math.min(Math.round(pendingActiveMs), MAX_ACTIVE_MS_PER_EVENT);
    pendingActiveMs = 0;
    return ms;
  };

  const enqueue = (type, fields = {}) => {
    if (!session) return;
    const event = { type, anonymousId: session.anonymousId, sessionId: session.sessionId, timestamp: now(), ...fields };
    queue = [...queue, { apiKey: session.apiKey, event }].slice(-MAX_QUEUE);
  };

  // Login / logout belong to no product or module, only to the page they happened on.
  const enqueueAuthEvent = (eventName) => enqueue("track", { eventName, url: page?.url });
  const enqueuePageView = () => page && enqueue("page_view", { url: page.url, ...page.context });
  const enqueuePageExit = () => page && enqueue("page_exit", { url: page.url, activeMs: takeActiveMs() });
  const enqueueHeartbeat = () => {
    const activeMs = page ? takeActiveMs() : 0;
    if (activeMs > 0) enqueue("heartbeat", { url: page.url, activeMs });
  };

  const flush = async ({ useBeacon = false } = {}) => {
    const ready = queue.filter((item) => item.apiKey);
    if (!ready.length) return;
    queue = queue.filter((item) => !item.apiKey);
    lastSentAt = now();

    const byKey = new Map();
    ready.forEach((item) => byKey.set(item.apiKey, [...(byKey.get(item.apiKey) ?? []), item]));
    const sends = [];
    byKey.forEach((items, apiKey) => {
      for (let i = 0; i < items.length; i += MAX_BATCH) {
        const chunk = items.slice(i, i + MAX_BATCH);
        const retryLater = () => (queue = [...chunk, ...queue].slice(-MAX_QUEUE));
        sends.push(sendEvents(apiKey, chunk.map((item) => item.event), { useBeacon }).then((ok) => ok || retryLater()));
      }
    });
    await Promise.all(sends);
  };

  // No heartbeat while idle: the server then closes the session after its
  // timeout, so "active now" on the dashboard means active.
  const heartbeat = () => {
    if (!session) return;
    enqueueHeartbeat();
    flush();
  };

  const onActivity = () => {
    const at = now();
    // Back after the server closed the session: re-open the page so its time lands on a page view.
    if (session && at - lastActivityAt > RESUME_AFTER_MS && at - lastSentAt > RESUME_AFTER_MS) {
      pendingActiveMs = 0;
      enqueuePageView();
      lastSentAt = at;
    }
    if (at - lastActivityAt > IDLE_TIMEOUT_MS) lastTickAt = at;
    lastActivityAt = at;
  };

  const onVisibilityChange = () => {
    if (!session) return;
    if (document.visibilityState === "hidden") {
      enqueueHeartbeat();
      flush({ useBeacon: true });
    } else {
      lastTickAt = now();
      onActivity();
    }
  };

  const onPageHide = () => {
    if (!session) return;
    enqueuePageExit();
    flush({ useBeacon: true });
  };

  const listen = () => {
    if (listening) return;
    listening = true;
    ANALYTICS_ACTIVITY_EVENTS.forEach((type) => document.addEventListener(type, onActivity, { passive: true, capture: true }));
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    // Fired by every logout path: logout button, idle logout, failed token refresh.
    window.addEventListener("logout", () => stop("logout"));
  };

  const stopTimers = () => {
    timers.forEach(clearInterval);
    timers = [];
  };

  const startTimers = () => {
    stopTimers();
    lastTickAt = now();
    timers = [setInterval(tick, TICK_MS), setInterval(heartbeat, FLUSH_INTERVAL_MS)];
  };

  const loadKey = async (tenantId, buId) => {
    const request = ++keyRequest;
    const { apiKey, anonymousId } = await getKey(tenantId, buId, session.userId);
    if (request !== keyRequest || !session) return;
    session.apiKey = apiKey;
    session.anonymousId = anonymousId;
    queue.forEach((item) => {
      if (!item.apiKey) {
        item.apiKey = apiKey;
        item.event.anonymousId = anonymousId;
      }
    });
  };

  async function stop(reason = "logout", { silent = false } = {}) {
    if (!session) return;
    if (silent) {
      queue = [];
    } else {
      if (reason === "logout") enqueueAuthEvent("logout");
      enqueuePageExit();
      enqueue("session_end", { url: page?.url, reason });
    }
    // Detached before the async flush, so a second stop() is a no-op.
    session = null;
    keyRequest++;
    stopTimers();
    pendingActiveMs = 0;
    storedSession.write(null);
    await flush({ useBeacon: true });
  }

  // One login stays one session across business-unit switches; only the key
  // (the BU's project) the events are sent with changes.
  async function setBusinessUnit(buId) {
    if (!session || !buId || session.buId === buId) return;
    enqueuePageExit();
    await flush();
    if (!session) return;
    session.buId = buId;
    session.apiKey = null;
    enqueuePageView();
    try {
      await loadKey(session.tenantId, buId);
    } catch (err) {
      console.warn("analytics: no tracking key for business unit", buId, err);
      queue = queue.filter((item) => item.apiKey);
    }
  }

  async function start({ userId, tenantId, buId } = {}) {
    if (!userId || !tenantId || !buId) return;
    if (session?.userId === userId && session.tenantId === tenantId) return setBusinessUnit(buId);
    if (session) await stop("user_changed");

    const stored = storedSession.read();
    const resumed = stored?.userId === userId && stored?.tenantId === tenantId; // a reload in the same tab
    session = { userId, tenantId, buId, sessionId: resumed ? stored.sessionId : uuid(), anonymousId: null, apiKey: null };
    storedSession.write({ userId, tenantId, sessionId: session.sessionId });

    listen();
    lastActivityAt = now();
    pendingActiveMs = 0;
    startTimers();
    if (!resumed) enqueueAuthEvent("login");
    enqueuePageView();

    try {
      await loadKey(tenantId, buId);
      // Send the login right away (not with the first minute's batch) so the
      // user's activity shows up on the dashboard straight after logging in.
      await flush();
    } catch (err) {
      console.warn("analytics: tracking disabled for this session", err);
      await stop("no_key", { silent: true });
    }
  }

  function setPage(url, context = {}) {
    if (!url) return;
    if (page?.url === url) {
      page.context = context;
      return;
    }
    enqueuePageExit();
    page = { url, context };
    enqueuePageView();
  }

  function track(eventName, properties = {}, { elementLabel, context } = {}) {
    if (!eventName) return;
    enqueue("track", {
      eventName: String(eventName).slice(0, 255),
      url: page?.url,
      ...page?.context,
      ...context,
      ...(elementLabel ? { elementLabel: String(elementLabel).slice(0, 255) } : {}),
      properties,
    });
  }

  // Kept for existing callers (e.g. admin_frontend's ClientController).
  function featureUsed(featureProps = {}) {
    const props = featureProps.featureProps ?? featureProps;
    track(props.event_name ?? "feature_used", props);
  }

  return {
    start,
    stop,
    setBusinessUnit,
    setPage,
    track,
    featureUsed,
    flush,
    getState: () => ({ session, queue, page }),
  };
}

export const initAnalyticsBridge = () => {
  if (!window.__ANALYTICS__) {
    const { start, stop, setBusinessUnit, setPage, track, featureUsed, flush } = createTracker();
    window.__ANALYTICS__ = { start, stop, setBusinessUnit, setPage, track, featureUsed, flush };
  }
  return window.__ANALYTICS__;
};
